/**
 * Human labels for FEA `parameter_sweeps` paths (ENG-2602).
 *
 * CFD axes are fixed strings in `SWEEP_PATH_LABEL_KEYS`. Structural paths are
 * instance-keyed (`boundary_conditions.face_0.vector_n.1`), so they need a
 * pattern parser — never show the raw wire path as primary UI copy.
 */

import type { TFunction } from 'i18next';

const AXIS_KEYS = [
  'simopsSolver.sweepPaths.axisFx',
  'simopsSolver.sweepPaths.axisFy',
  'simopsSolver.sweepPaths.axisFz',
] as const;

const AXIS_SHORT = ['Fx', 'Fy', 'Fz'] as const;

/**
 * Localized label for a structural sweep path, or `null` when the path is
 * not a known FEA axis shape (caller falls back to the raw path / CFD map).
 */
export function structuralSweepPathLabel(
  path: string,
  t: TFunction,
): string | null {
  const parts = path.split('.');

  if (parts[0] === 'contact_pairs' && parts.length === 3) {
    const [, name, field] = parts;
    if (field === 'friction_coefficient') {
      return t('simopsSolver.sweepPaths.contactFriction', { name });
    }
    if (field === 'stiffness_mpa_per_mm') {
      return t('simopsSolver.sweepPaths.contactStiffness', { name });
    }
    if (field === 'stick_slope_mpa_per_mm') {
      return t('simopsSolver.sweepPaths.contactStickSlope', { name });
    }
  }

  if (
    (parts[0] === 'bolt_shrink_preloads' || parts[0] === 'bolt_preloads') &&
    parts.length === 3 &&
    parts[2] === 'force_n'
  ) {
    return t('simopsSolver.sweepPaths.boltPreload', { name: parts[1] });
  }

  if (
    parts[0] === 'tied_interfaces' &&
    parts.length === 3 &&
    parts[2] === 'position_tolerance_mm'
  ) {
    return t('simopsSolver.sweepPaths.tieTolerance', { name: parts[1] });
  }

  if (parts[0] === 'boundary_conditions' && parts.length === 3) {
    const [, target, field] = parts;
    if (field === 'pressure_MPa') {
      return t('simopsSolver.sweepPaths.facePressure', { target });
    }
    if (field === 'value') {
      return t('simopsSolver.sweepPaths.thermalValue', { target });
    }
    if (field === 'h') {
      return t('simopsSolver.sweepPaths.convectionH', { target });
    }
    if (field === 't_ambient') {
      return t('simopsSolver.sweepPaths.ambientTemperature', { target });
    }
    if (field === 'emissivity') {
      return t('simopsSolver.sweepPaths.radiationEmissivity', { target });
    }
    if (field === 'magnitude_m_s2' && target === 'gravity') {
      return t('simopsSolver.sweepPaths.gravityMagnitude');
    }
    if (field === 'rpm' && target === 'centrifugal') {
      return t('simopsSolver.sweepPaths.centrifugalRpm');
    }
  }

  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 4 &&
    parts[2] === 'vector_n'
  ) {
    const axis = Number(parts[3]);
    if (!Number.isInteger(axis) || axis < 0 || axis > 2) return null;
    const target = parts[1];
    const axisLabel = t(AXIS_KEYS[axis]);
    return t('simopsSolver.sweepPaths.loadVector', { target, axis: axisLabel });
  }

  return null;
}

/** Compact chart / column short label (locale-invariant symbols where possible). */
export function structuralSweepPathShortLabel(path: string): string | null {
  const parts = path.split('.');
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 4 &&
    parts[2] === 'vector_n'
  ) {
    const axis = Number(parts[3]);
    if (!Number.isInteger(axis) || axis < 0 || axis > 2) return null;
    return `${parts[1]} · ${AXIS_SHORT[axis]}`;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[2] === 'pressure_MPa'
  ) {
    return `${parts[1]} · P`;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[2] === 'value'
  ) {
    return `${parts[1]} · T`;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[2] === 'h'
  ) {
    return `${parts[1]} · h`;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[2] === 't_ambient'
  ) {
    return `${parts[1]} · T∞`;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[2] === 'emissivity'
  ) {
    return `${parts[1]} · ε`;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[1] === 'gravity' &&
    parts[2] === 'magnitude_m_s2'
  ) {
    return 'g · |a|';
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[1] === 'centrifugal' &&
    parts[2] === 'rpm'
  ) {
    return 'ω · rpm';
  }
  if (parts[0] === 'contact_pairs' && parts.length === 3) {
    if (parts[2] === 'friction_coefficient') return `${parts[1]} · μ`;
    if (parts[2] === 'stiffness_mpa_per_mm') return `${parts[1]} · k`;
    if (parts[2] === 'stick_slope_mpa_per_mm') return `${parts[1]} · stick`;
  }
  if (
    (parts[0] === 'bolt_shrink_preloads' || parts[0] === 'bolt_preloads') &&
    parts.length === 3 &&
    parts[2] === 'force_n'
  ) {
    return `${parts[1]} · F`;
  }
  if (
    parts[0] === 'tied_interfaces' &&
    parts.length === 3 &&
    parts[2] === 'position_tolerance_mm'
  ) {
    return `${parts[1]} · tol`;
  }
  return null;
}
